import { useEffect, useRef, useState } from 'react';
import { useExperimentStore } from '../store/experimentStore';
import { useExperimentConfig } from '../store/experimentConfigStore';
import { getPhaseLabel, getEquivalenceVolume } from '../lib/chemistry';
import { Download, Plus } from 'lucide-react';

export default function DataTable() {
    const titrationData = useExperimentStore((state) => state.titrationData);
    const hclConcentration = useExperimentStore((state) => state.hclConcentration);
    const naohConcentration = useExperimentStore((state) => state.naohConcentration);
    const flaskVolume = useExperimentStore((state) => state.flaskVolume);
    const currentPH = useExperimentStore((state) => state.currentPH);
    const volumeAdded = useExperimentStore((state) => state.volumeAdded);
    const addManualDataPoint = useExperimentStore((state) => state.addManualDataPoint);

    const shouldAutoFill = useExperimentConfig((state) => state.shouldAutoFill());
    const allowPlaceholders = useExperimentConfig((s) => s.currentConfig?.autoFillData || s.isTeacherMode);
    const [manualVolume, setManualVolume] = useState('');
    const [manualPH, setManualPH] = useState('');

    const scrollRef = useRef<HTMLDivElement>(null);

    const equivVol = getEquivalenceVolume(hclConcentration, flaskVolume, naohConcentration);

    // Auto-scroll to bottom when data changes
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [titrationData]);

    const handleExportCSV = () => {
        const headers = ['Entry', 'Volume Added (mL)', 'pH', 'Phase'];
        const rows = titrationData.map((d, i) => [
            i + 1,
            d.volume.toFixed(2),
            d.ph.toFixed(2),
            getPhaseLabel(d.ph)
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(e => e.join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'titration_data.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Find the row closest to pH 7.0 for highlighting
    let closestIndex = -1;
    let minDiff = Infinity;
    titrationData.forEach((d, i) => {
        const diff = Math.abs(d.ph - 7.0);
        if (diff < minDiff) {
            minDiff = diff;
            closestIndex = i;
        }
    });

    const handleAddManualPoint = (e: React.FormEvent) => {
        e.preventDefault();
        const vol = parseFloat(manualVolume);
        const phVal = parseFloat(manualPH);
        if (!isNaN(vol) && !isNaN(phVal)) {
            addManualDataPoint(vol, phVal);
            setManualVolume('');
            setManualPH('');
        }
    };

    return (
        <div className="flex flex-col h-full text-foreground relative">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-xl font-bold tracking-tight">Live Data Log</h2>
                    <p className="text-xs text-indigo-600 dark:text-indigo-300/70 font-medium uppercase tracking-wider mt-1">Target: ~{equivVol.toFixed(2)} mL</p>
                </div>
                <button
                    onClick={handleExportCSV}
                    disabled={titrationData.length === 0}
                    className="interactable-btn flex items-center gap-2 bg-muted/50 border border-border hover:bg-muted hover:border-border/80 text-xs py-1.5 px-3 rounded-lg text-foreground disabled:opacity-50 transition-all font-medium shadow-sm"
                >
                    <Download className="w-3 h-3" />
                    Export CSV
                </button>
            </div>

            <div className="flex-1 overflow-hidden flex flex-col border border-indigo-500/20 rounded-2xl bg-card/50 shadow-inner backdrop-blur-sm relative">
                {/* Table Header */}
                <div className="grid grid-cols-4 gap-2 px-4 py-3 border-b border-indigo-500/20 bg-indigo-500/5 text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase tracking-widest sticky top-0 z-10 backdrop-blur-md">
                    <div className="text-center">#</div>
                    <div className="text-right">Vol (mL)</div>
                    <div className="text-right">pH</div>
                    <div className="text-center">Phase</div>
                </div>

                {/* Table Body — capped height, always scrolls */}
                <div ref={scrollRef} className={`flex-1 overflow-y-auto w-full customized-scrollbar ${shouldAutoFill ? 'pb-2' : 'pb-24'}`}>
                    {titrationData.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full pt-12 text-indigo-600/50 dark:text-indigo-300/50 space-y-3">
                            <div className="w-8 h-8 rounded-full border border-indigo-500/30 border-t-indigo-400 animate-spin" />
                            <div className="text-sm font-medium tracking-wide">Awaiting data points...</div>
                        </div>
                    ) : (
                        <div className="flex flex-col pb-2">
                            {titrationData.map((d, i) => {
                                const phase = getPhaseLabel(d.ph);
                                const isClosest = i === closestIndex && Math.abs(d.ph - 7.0) < 1.0;

                                let phaseColor = 'text-green-400 bg-green-500/10 border-green-500/20';
                                if (phase === 'Acidic') phaseColor = 'text-red-400 bg-red-500/10 border-red-500/20';
                                if (phase === 'Basic') phaseColor = 'text-blue-400 bg-blue-500/10 border-blue-500/20';

                                return (
                                    <div
                                        key={i}
                                        className={`grid grid-cols-4 gap-2 px-4 py-2.5 border-b border-border/50 text-sm hover:bg-muted/50 transition-colors items-center
                      ${isClosest ? 'bg-indigo-500/10 dark:bg-indigo-500/20 border-l-2 border-l-indigo-400 shadow-[inset_0_0_15px_rgba(99,102,241,0.15)] relative overflow-hidden' : ''}`}
                                    >
                                        {isClosest && <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/10 to-transparent pointer-events-none" />}
                                        <div className="text-center text-muted-foreground font-mono text-xs">{i + 1}</div>
                                        <div className="text-right font-mono text-muted-foreground relative z-10">{d.volume.toFixed(2)}</div>
                                        <div className="text-right font-mono font-bold text-foreground relative z-10 drop-shadow-md">{d.ph.toFixed(2)}</div>
                                        <div className="flex justify-center relative z-10">
                                            <div className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-md border ${phaseColor}`}>
                                                {phase}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Manual Entry Form */}
                {!shouldAutoFill && (
                    <div className="absolute top-1/2 left-0 w-full z-20 pointer-events-none flex flex-col justify-end" style={{ top: 'auto', bottom: 0 }}>
                        {/* Gradient overlay to smoothly transition from scrollable table to sticky footer */}
                        <div className="h-8 bg-gradient-to-t from-card/95 flex-shrink-0" />
                        <div className="bg-card/95 backdrop-blur-md border-t border-indigo-500/20 p-3 pointer-events-auto">
                            <form onSubmit={handleAddManualPoint} className="flex gap-2 items-end">
                                <div className="flex-1">
                                    <label className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider mb-1 block">Volume (mL)</label>
                                    <input 
                                        type="number" 
                                        step="0.01" 
                                        required 
                                        placeholder={allowPlaceholders ? volumeAdded.toFixed(2) : ''}
                                        value={manualVolume}
                                        onChange={(e) => setManualVolume(e.target.value)}
                                        className="w-full bg-background border border-border rounded-lg px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500/50"
                                    />
                                </div>
                                <div className="flex-1">
                                    <label className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider mb-1 block">pH Level</label>
                                    <input 
                                        type="number" 
                                        step="0.01" 
                                        required 
                                        placeholder={allowPlaceholders ? currentPH.toFixed(2) : ''}
                                        value={manualPH}
                                        onChange={(e) => setManualPH(e.target.value)}
                                        className="w-full bg-background border border-border rounded-lg px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500/50"
                                    />
                                </div>
                                <button 
                                    type="submit"
                                    className="interactable-btn bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg p-2 transition-colors flex items-center justify-center shadow-lg shadow-indigo-600/20 mb-0.5"
                                >
                                    <Plus className="w-5 h-5" />
                                </button>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
